import { APP_BASE_HREF, CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, Optional, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { RestService } from '../service/rest/rest.service';
import { LoggerService } from '../service/utils/logger.service';
import { LegalFooterComponent } from '../legal/legal-footer.component';
import { PRIVACY_VERSION, TERMS_VERSION } from '../legal/legal-content';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, LegalFooterComponent],
  templateUrl: './signup.component.html',
  styleUrls: ['../login/login.component.css'],
})
export class SignupComponent {
  form: FormGroup;
  loading = false;
  message = '';
  error = '';
  protected readonly brand = inject(BrandService);

  constructor(
    private fb: FormBuilder,
    private restService: RestService,
    @Inject(DOCUMENT) private doc: Document,
    @Optional() @Inject(APP_BASE_HREF) private appBaseHref: string | null,
  ) {
    this.form = this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]],
        termsAccepted: [false, Validators.requiredTrue],
      },
      { validators: SignupComponent.passwordsMatch },
    );
  }

  private static passwordsMatch(control: AbstractControl): ValidationErrors | null {
    const p = control.get('password')?.value;
    const c = control.get('confirmPassword')?.value;
    return p && c && p !== c ? { mismatch: true } : null;
  }

  private loginRedirectUrl(): string {
    const win = this.doc.defaultView;
    if (!win) return '';
    const origin = win.location.origin;
    const base = (this.appBaseHref ?? '/').replace(/\/?$/, '/');
    return `${origin}${base}login?signup=confirmed`;
  }

  async onSubmit(): Promise<void> {
    this.message = '';
    this.error = '';
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    try {
      await this.restService.supabaseSignUp(
        String(this.form.value.email || '').trim(),
        String(this.form.value.password || ''),
        this.loginRedirectUrl(),
        {
          termsVersion: TERMS_VERSION,
          privacyVersion: PRIVACY_VERSION,
          acceptedAt: new Date().toISOString(),
        },
      );
      this.message = 'Account created. Check your email to confirm the account, then sign in.';
      this.form.reset();
    } catch (e) {
      LoggerService.error('Sign-up failed', e);
      this.error = e instanceof Error && e.message
        ? e.message
        : 'Could not create the account. The email may already be registered or sign-up may be disabled.';
    } finally {
      this.loading = false;
    }
  }
}
